import {useCallback} from 'react'
import {Rectangle} from 'recharts'

import type {ProspectClickFilters} from 'src/components/analytics/use-prospect-navigation'
import {CHART_COLORS} from 'src/lib/chart-colors'

export const DEFAULT_VERTICAL_BAR_CHART_LAYOUT = {
    chartHeight: 320,
    marginTop: 24,
    marginBottom: 60,
} as const

type VerticalBarChartClickLayout = {
    chartHeight: number
    marginTop: number
    marginBottom: number
}

/**
 * Custom Recharts Bar shape: real bar plus a transparent column for the full plot
 * height so short bars are easy to click.
 */
export function useCapacityBucketVerticalBarShape(
    onProspectFilter: ((filters: ProspectClickFilters) => void) | undefined,
    layout: VerticalBarChartClickLayout = DEFAULT_VERTICAL_BAR_CHART_LAYOUT,
) {
    const plotHeight = layout.chartHeight - layout.marginTop - layout.marginBottom
    return useCallback(
        (props: unknown) => {
            const p = props as {
                x?: number
                y?: number
                width?: number
                height?: number
                fill?: string
                radius?: [number, number, number, number]
                payload?: {capacityBucket?: string}
            }
            const {x = 0, y = 0, width = 0, height = 0, fill, radius, payload} = p
            const bucket = payload?.capacityBucket

            return (
                <g>
                    <Rectangle
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={fill ?? CHART_COLORS.secondary}
                        radius={radius ?? [4, 4, 0, 0]}
                        style={onProspectFilter && bucket ? {cursor: 'pointer'} : undefined}
                    />
                    {onProspectFilter && bucket && (
                        <rect
                            x={x}
                            y={0}
                            width={width}
                            height={plotHeight}
                            fill="transparent"
                            style={{cursor: 'pointer'}}
                            onClick={() => onProspectFilter({capacityBucket: bucket})}
                        />
                    )}
                </g>
            )
        },
        [onProspectFilter, plotHeight],
    )
}
